import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Grid,
  makeStyles,
  Paper,
  Tab,
  Typography,
} from '@material-ui/core';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { Person } from '@material-ui/icons';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { RootState } from 'interfaces';
import { downloadFile, USER_OPERATIONS } from 'helper';
import { ITypeTechnicalRoom } from 'pages/PageTypeTechnicalRoomPprList/interfaces';
import { getTypeTechnicalRoomListByRealEstate } from 'pages/PageTypeTechnicalRoomPprList/api';
import ConfirmDialog from 'dialogs/ConfirmDialog';
import useError from 'components/ErrorProvider/useError';

import {
  changeJournalEquipmentStatus,
  createJournalEquipmentItemWithTypeTechnicalRoom,
  createResponsiblePersonList,
  deleteJournalEquipmentItem,
  exportPdf,
  exportXls,
  loadJournalEquipment,
  updateJournalEquipmentItem,
  updateJournalEquipmentItemWithTypeTechnicalRoom,
  updateResponsiblePersonList,
  updateEquipmentStatusToChecked,
  writeOffJournalEquipmentItem,
  createEquipmentReplacement,
} from './api';
import {
  EJournalEquipmentStatuses,
  IEquipmentType,
  IJournalEquipment,
  IJournalEquipmentItem,
  IPosition,
  IResponsiblePersonList,
} from './interfaces';
import {
  CancelAgreementDialog,
  JournalEquipmentItemSaveDialog,
  JournalEquipmentHistoryDialog,
  ResponsiblePersonListDialog,
  JournalEquipment,
  JournalEquipmentItemWriteOffDialog,
  JournalEquipmentItemReplaceDialog,
} from './components';
import { modifyJournalEquipment } from './helpers';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    padding: 20,
    backgroundColor: '#fff',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    zIndex: 1000,
  },
});

type CancelAgreementReason = 'Отправить на доработку' | 'Отозвать';

interface IProps {
  positions: IPosition[];
  equipmentTypes: IEquipmentType[];
  realEstateId: string;
}

const View: React.FC<IProps> = ({ positions, equipmentTypes, realEstateId }) => {
  // Global state
  const user = useSelector((state: RootState) => state.user);
  const operations = useSelector((state: RootState) => state.operations);

  // Local state
  const [tabIndex, setTabIndex] = useState('0');
  const [journalEquipment, setJournalEquipment] = useState<IJournalEquipment | null>(null);
  const [currentJournalEquipmentItem, setCurrentJournalEquipmentItem] = useState<IJournalEquipmentItem | null>(null);
  const [currentResponsiblePersonList, setCurrentResponsiblePersonList] = useState<IResponsiblePersonList | null>(null);
  const [responsiblePersonListDialogOpened, setOpenedResponsiblePersonListDialog] = useState(false);

  const [cancelAgreementReason, setCancelAgreementReason] = useState<CancelAgreementReason | null>(null);
  const [agreementDialogOpened, setOpenedAgreementDialog] = useState(false);

  const [deleteJournalEquipmentItemDialogOpened, setOpenedDeleteJournalEquipmentItemDialog] = useState(false);
  const [journalEquipmentHistoryDialogOpened, setOpenedJournalEquipmentHistoryDialog] = useState(false);
  const [journalEquipmentItemDialogOpened, setOpenedJournalEquipmentItemDialog] = useState(false);
  const [equipmentWriteOffDialogOpened, setOpenedEquipmentWriteOffDialog] = useState(false);
  const [equipmentAddDialogOpened, setOpenedEquipmentAddDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  // Styles
  const classes = useStyles();

  // Global notification
  const { enqueueSnackbar } = useSnackbar();
  const { addError } = useError();

  // Methods
  const handleSubmitResponsiblePersonList = async (responsiblePersonList: IResponsiblePersonList) => {
    setDisabled(true);

    try {
      currentResponsiblePersonList
        ? await updateResponsiblePersonList(realEstateId, { realEstateId, ...responsiblePersonList })
        : await createResponsiblePersonList({ realEstateId, ...responsiblePersonList });

      setOpenedResponsiblePersonListDialog(false);
      !currentResponsiblePersonList &&
        enqueueSnackbar('Журнал учета оборудования для текущего ЖК успешно создан', {
          variant: 'success',
        });
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleSubmitAgreement = async (agree: boolean, comment?: string) => {
    if (!journalEquipment?.journalEquipmentId) return;
    setDisabled(true);

    try {
      await changeJournalEquipmentStatus(journalEquipment.journalEquipmentId, agree, comment);

      setOpenedAgreementDialog(false);
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleSubmitJournalEquipmentItem = async (journalEquipmentItem: IJournalEquipmentItem) => {
    if (!journalEquipment?.journalEquipmentId) return;
    setDisabled(true);

    try {
      currentJournalEquipmentItem?.journalEquipmentItemId
        ? journalEquipmentItem.typeTechnicalRoomId
          ? await updateJournalEquipmentItemWithTypeTechnicalRoom(
              journalEquipment.journalEquipmentId,
              journalEquipmentItem.typeTechnicalRoomId,
              journalEquipmentItem,
            )
          : await updateJournalEquipmentItem(journalEquipment.journalEquipmentId, journalEquipmentItem)
        : journalEquipmentItem.typeTechnicalRoomId &&
          (await createJournalEquipmentItemWithTypeTechnicalRoom(
            journalEquipment.journalEquipmentId,
            journalEquipmentItem.typeTechnicalRoomId,
            journalEquipmentItem,
          ));

      handleCloseJournalEquipmentItemDialog();
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleDeleteJournalEquipmentItem = async (journalEquipmentItem: IJournalEquipmentItem) => {
    if (!journalEquipment?.journalEquipmentId) return;
    setDisabled(true);

    try {
      await deleteJournalEquipmentItem(journalEquipment.journalEquipmentId, journalEquipmentItem);

      handleCloseDeleteJournalEquipmentItemDialog();
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleMarkAsChecked = async (journalEquipmentItem: IJournalEquipmentItem) => {
    if (!journalEquipmentItem.journalEquipmentItemId) return;
    setDisabled(true);

    try {
      await updateEquipmentStatusToChecked(journalEquipmentItem.journalEquipmentItemId);
      enqueueSnackbar('Статус оборудования обновлен на "Проверено"', { variant: 'success' });
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleOpenEquipmentReplacementDialog = (journalEquipmentItem: IJournalEquipmentItem) => {
    setCurrentJournalEquipmentItem({ ...journalEquipmentItem });
    setOpenedEquipmentWriteOffDialog(true);
  };

  const handleCloseEquipmentWriteOffDialog = () => {
    setOpenedEquipmentWriteOffDialog(false);
    setTimeout(() => setCurrentJournalEquipmentItem(null), 150);
  };

  const handleOpenEquipmentAddDialog = (journalEquipmentItem: IJournalEquipmentItem) => {
    // Передаем только нужные поля для создания нового оборудования
    setCurrentJournalEquipmentItem({
      journalEquipmentItemId: journalEquipmentItem.journalEquipmentItemId,
      typeTechnicalRoomId: journalEquipmentItem.typeTechnicalRoomId,
      equipmentTypeId: journalEquipmentItem.equipmentTypeId,
      equipmentTypeName: journalEquipmentItem.equipmentTypeName,
    } as IJournalEquipmentItem);
    setOpenedEquipmentAddDialog(true);
  };

  const handleCloseEquipmentAddDialog = () => {
    setOpenedEquipmentAddDialog(false);
    setTimeout(() => setCurrentJournalEquipmentItem(null), 150);
  };

  const handleWriteOff = async (countWrittenOff: number) => {
    if (!currentJournalEquipmentItem?.journalEquipmentItemId) return;
    setDisabled(true);

    try {
      await writeOffJournalEquipmentItem({
        journalEquipmentItemId: currentJournalEquipmentItem.journalEquipmentItemId,
        countWrittenOff,
      });

      enqueueSnackbar('Оборудование успешно списано', { variant: 'success' });
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
      throw error; // Пробрасываем ошибку, чтобы диалог не переходил на следующий шаг
    } finally {
      setDisabled(false);
    }
  };

  const handleAddReplacement = async (newEquipment: IJournalEquipmentItem) => {
    if (!journalEquipment?.journalEquipmentId) return;
    setDisabled(true);

    try {
      await createEquipmentReplacement(
        journalEquipment.journalEquipmentId,
        currentJournalEquipmentItem!.typeTechnicalRoomId!,
        newEquipment,
      );

      enqueueSnackbar('Новое оборудование успешно добавлено', { variant: 'success' });
      handleCloseEquipmentWriteOffDialog();
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleAddEquipment = async (newEquipment: IJournalEquipmentItem) => {
    if (!currentJournalEquipmentItem?.typeTechnicalRoomId || !journalEquipment?.journalEquipmentId) return;
    setDisabled(true);

    try {
      await createEquipmentReplacement(
        journalEquipment.journalEquipmentId,
        currentJournalEquipmentItem.typeTechnicalRoomId!,
        newEquipment,
      );

      enqueueSnackbar('Новое оборудование успешно добавлено', { variant: 'success' });
      handleCloseEquipmentAddDialog();
      await fetchJournalEquipment();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleExportXls = async () => {
    setDisabled(true);

    try {
      await exportXls(realEstateId).then((response) =>
        downloadFile(
          response,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `${moment(new Date(), 'dd-MM-yyyy')}.xls`,
        ),
      );
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleExportPdf = async () => {
    setDisabled(true);

    try {
      await exportPdf(realEstateId).then((response) =>
        downloadFile(response, 'application/pdf', `${moment(new Date(), 'dd-MM-yyyy')}.pdf`),
      );
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleOpenResponsiblePersonListDialog = () => {
    setOpenedResponsiblePersonListDialog(true);

    if (!journalEquipment) return;
    const {
      headUserId,
      headUserName,
      headPositionId,
      headPositionName,
      executorHeadUserId,
      executorHeadUserName,
      executorHeadPositionId,
      executorHeadPositionName,
      executorUserId,
      executorUserName,
      executorPositionId,
      executorPositionName,
      supervisorName,
      supervisorUserId,
    } = journalEquipment;

    setCurrentResponsiblePersonList({
      headUser: { username: headUserId, fullName: headUserName },
      headPosition: { positionId: headPositionId, positionName: headPositionName },
      executorHeadUser: { username: executorHeadUserId, fullName: executorHeadUserName },
      executorHeadPosition: { positionId: executorHeadPositionId, positionName: executorHeadPositionName },
      executorUser: { username: executorUserId, fullName: executorUserName },
      executorPosition: { positionId: executorPositionId, positionName: executorPositionName },
      supervisor: { username: supervisorUserId, fullName: supervisorName },
    });
  };

  const handleOpenAgreementDialog = (reason: CancelAgreementReason) => {
    setCancelAgreementReason(reason);
    setOpenedAgreementDialog(true);
  };

  const handleOpenJournalEquipmentItemDialog = (
    typeTechnicalRoom: ITypeTechnicalRoom,
    journalEquipmentItem: IJournalEquipmentItem,
  ) => {
    // Если это новое оборудование (нет journalEquipmentItemId), передаем только базовые поля
    if (!journalEquipmentItem.journalEquipmentItemId) {
      setOpenedJournalEquipmentItemDialog(true);
      setCurrentJournalEquipmentItem({
        typeTechnicalRoomId: typeTechnicalRoom.typeTechnicalRoomId,
        typeTechnicalRoomName: typeTechnicalRoom.typeTechnicalRoomName,
        equipmentTypeId: journalEquipmentItem.equipmentTypeId,
        equipmentTypeName: journalEquipmentItem.equipmentTypeName,
      } as IJournalEquipmentItem);
      return;
    }

    // Если редактируем существующее оборудование, передаем все данные
    const { equipmentTypeId, equipmentTypeName, equipmentId, equipmentName, ...props } = journalEquipmentItem;

    setOpenedJournalEquipmentItemDialog(true);
    setCurrentJournalEquipmentItem({
      ...typeTechnicalRoom,
      equipmentType: { equipmentTypeId, equipmentTypeName },
      equipment: { equipmentId, equipmentName },
      ...props,
      equipmentSerialNumberItem: '',
    });
  };

  const handleOpenDeleteJournalEquipmentItemDialog = (
    typeTechnicalRoom: ITypeTechnicalRoom,
    journalEquipmentItem: IJournalEquipmentItem,
  ) => {
    setOpenedDeleteJournalEquipmentItemDialog(true);
    setCurrentJournalEquipmentItem({ ...typeTechnicalRoom, ...journalEquipmentItem });
  };

  const handleCloseResponsiblePersonListDialog = () => {
    setOpenedResponsiblePersonListDialog(false);
    setCurrentResponsiblePersonList(null);
  };

  const handleCloseJournalEquipmentItemDialog = () => {
    setOpenedJournalEquipmentItemDialog(false);
    setTimeout(() => setCurrentJournalEquipmentItem(null), 150);
  };

  const handleCloseDeleteJournalEquipmentItemDialog = () => {
    setOpenedDeleteJournalEquipmentItemDialog(false);
    setCurrentJournalEquipmentItem(null);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: string): void => {
    if (tabIndex === newValue) return;

    setTabIndex(newValue);
  };

  async function fetchJournalEquipment() {
    setLoading(true);

    try {
      const typeTechnicalRooms = await getTypeTechnicalRoomListByRealEstate(realEstateId);
      await loadJournalEquipment(realEstateId).then((journalEquipment) =>
        setJournalEquipment(modifyJournalEquipment(typeTechnicalRooms, journalEquipment)),
      );
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setLoading(false);
    }
  }

  // Side effects
  useEffect(() => {
    fetchJournalEquipment();
  }, [realEstateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isExecutorAgreement =
      [EJournalEquipmentStatuses.CREATE, EJournalEquipmentStatuses.REVISION_AGREEMENT].includes(
        journalEquipment?.journalEquipmentStatusId!,
      ) && !journalEquipment?.executorAgreement,
    isExecutorHeadAgreement =
      ![EJournalEquipmentStatuses.CREATE, EJournalEquipmentStatuses.AGREEMENT].includes(
        journalEquipment?.journalEquipmentStatusId!,
      ) &&
      journalEquipment?.executorAgreement &&
      !journalEquipment?.executorHeadAgreement,
    isHeadAgreement =
      ![
        EJournalEquipmentStatuses.CREATE,
        EJournalEquipmentStatuses.REVISION_AGREEMENT,
        EJournalEquipmentStatuses.AGREEMENT,
      ].includes(journalEquipment?.journalEquipmentStatusId!) &&
      journalEquipment?.executorAgreement &&
      journalEquipment?.executorHeadAgreement &&
      !journalEquipment?.headAgreement,
    isSupervisorAgreement = [EJournalEquipmentStatuses.ON_AGREEMENT].includes(
      journalEquipment?.journalEquipmentStatusId!,
    );

  const canRevoke =
    ([EJournalEquipmentStatuses.ON_AGREEMENT].includes(journalEquipment?.journalEquipmentStatusId!) &&
      journalEquipment?.executorUserId === user.username &&
      isExecutorHeadAgreement &&
      !journalEquipment.executorHeadAgreement) ||
    (journalEquipment?.executorHeadUserId === user.username && isHeadAgreement && !journalEquipment.headAgreement);

  const canManageItemStatus = operations.includes(USER_OPERATIONS.JOURNAL_EQUIPMENT_ITEM_STATUS);

  // Main render
  return (
    <>
      <Box className={classes.root}>
        <Grid container spacing={2}>
          {!loading && journalEquipment?.journalEquipmentId && (
            <Grid item xs={12}>
              <Typography color="primary">
                Статус журнала: <b>{journalEquipment?.journalEquipmentStatusName}</b>
              </Typography>
            </Grid>
          )}

          {!loading && (
            <Grid item xs={12}>
              <ButtonGroup>
                <Button
                  variant="outlined"
                  startIcon={<Person />}
                  onClick={handleOpenResponsiblePersonListDialog}
                  disabled={disabled}
                >
                  {journalEquipment ? 'Ответственные лица' : 'Назначить ответственных лиц'}
                </Button>

                {journalEquipment?.supervisorUserId === user.username && isSupervisorAgreement && (
                  <Button
                    variant="outlined"
                    onClick={() => handleOpenAgreementDialog('Отправить на доработку')}
                    disabled={disabled}
                  >
                    Отправить на доработку
                  </Button>
                )}

                {canRevoke ? (
                  <Button variant="outlined" onClick={() => handleOpenAgreementDialog('Отозвать')} disabled={disabled}>
                    Отозвать
                  </Button>
                ) : (
                  journalEquipment?.executorUserId === user.username &&
                  isExecutorAgreement && (
                    <Button variant="outlined" onClick={() => handleSubmitAgreement(true)} disabled={disabled}>
                      Отправить на согласование
                    </Button>
                  )
                )}

                {((journalEquipment?.executorHeadUserId === user.username && isExecutorHeadAgreement) ||
                  (journalEquipment?.headUserId === user.username && isHeadAgreement)) && [
                  <Button
                    key="Согласовать"
                    variant="outlined"
                    onClick={() => handleSubmitAgreement(true)}
                    disabled={disabled}
                  >
                    Согласовать
                  </Button>,

                  !canRevoke && (
                    <Button
                      key="Отправить на доработку"
                      variant="outlined"
                      onClick={() => handleOpenAgreementDialog('Отправить на доработку')}
                      disabled={disabled}
                    >
                      Отправить на доработку
                    </Button>
                  ),
                ]}

                {Boolean(journalEquipment?.history.length) && (
                  <Button
                    variant="outlined"
                    onClick={() => setOpenedJournalEquipmentHistoryDialog(true)}
                    disabled={disabled}
                  >
                    История изменений
                  </Button>
                )}
              </ButtonGroup>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ position: 'relative' }}>
              {loading ? (
                <Paper variant="outlined">
                  <Box sx={{ p: 5 }}>
                    <Box className={classes.loader}>
                      <CircularProgress />
                    </Box>
                  </Box>
                </Paper>
              ) : (
                journalEquipment && (
                  <TabContext value={tabIndex}>
                    <TabList variant="scrollable" onChange={handleChangeTab}>
                      {journalEquipment.modifiedItems.map(({ typeTechnicalRoom }, index) => (
                        <Tab
                          key={index}
                          value={String(index)}
                          label={typeTechnicalRoom?.typeTechnicalRoomName ?? 'Все'}
                        />
                      ))}
                    </TabList>

                    {journalEquipment.modifiedItems.map(({ typeTechnicalRoom, equipmentTypes }, index) => (
                      <TabPanel value={String(index)} key={index}>
                        <JournalEquipment
                          typeTechnicalRoom={typeTechnicalRoom}
                          equipmentTypes={equipmentTypes}
                          journalEquipmentStatusId={journalEquipment.journalEquipmentStatusId}
                          disabled={disabled}
                          canManageItemStatus={canManageItemStatus}
                          onOpenJournalEquipmentItemDialog={handleOpenJournalEquipmentItemDialog}
                          onOpenDeleteJournalEquipmentItemDialog={handleOpenDeleteJournalEquipmentItemDialog}
                          onMarkAsChecked={handleMarkAsChecked}
                          onOpenEquipmentReplacementDialog={handleOpenEquipmentReplacementDialog}
                          onOpenEquipmentAddDialog={handleOpenEquipmentAddDialog}
                          onExportXls={handleExportXls}
                          onExportPdf={handleExportPdf}
                        />
                      </TabPanel>
                    ))}
                  </TabContext>
                )
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <ResponsiblePersonListDialog
        open={responsiblePersonListDialogOpened}
        responsiblePersonList={currentResponsiblePersonList!}
        positions={positions}
        disabled={disabled}
        onSubmitResponsiblePersonList={handleSubmitResponsiblePersonList}
        onClose={handleCloseResponsiblePersonListDialog}
      />

      <CancelAgreementDialog
        open={agreementDialogOpened}
        title={cancelAgreementReason!}
        disabled={disabled}
        onSubmitAgreement={(comment) => handleSubmitAgreement(false, comment)}
        onClose={() => setOpenedAgreementDialog(false)}
      />

      <JournalEquipmentHistoryDialog
        open={journalEquipmentHistoryDialogOpened}
        history={journalEquipment?.history}
        disabled={disabled}
        onClose={() => setOpenedJournalEquipmentHistoryDialog(false)}
      />

      <ConfirmDialog
        open={deleteJournalEquipmentItemDialogOpened}
        title="Вы действительно хотите удалить запись из журнала?"
        disabled={disabled}
        onConfirm={() => handleDeleteJournalEquipmentItem(currentJournalEquipmentItem!)}
        onClose={handleCloseDeleteJournalEquipmentItemDialog}
      />

      <JournalEquipmentItemSaveDialog
        open={journalEquipmentItemDialogOpened}
        journalEquipmentItem={currentJournalEquipmentItem}
        disabled={disabled}
        onSubmitJournalEquipmentItem={handleSubmitJournalEquipmentItem}
        onClose={handleCloseJournalEquipmentItemDialog}
      />

      <JournalEquipmentItemWriteOffDialog
        open={equipmentWriteOffDialogOpened}
        equipmentItem={currentJournalEquipmentItem}
        disabled={disabled}
        onClose={handleCloseEquipmentWriteOffDialog}
        onWriteOff={handleWriteOff}
        onAddReplacement={handleAddReplacement}
      />

      <JournalEquipmentItemReplaceDialog
        open={equipmentAddDialogOpened}
        equipmentItem={currentJournalEquipmentItem}
        disabled={disabled}
        onClose={handleCloseEquipmentAddDialog}
        onSubmit={handleAddEquipment}
      />
    </>
  );
};

export default View;
